import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import WarningBanner from 'components/WarningBanner';
import FileCheckSelector from 'router/subrouters/DataRoom/components/FileCheckSelector';
import FileRowPlaceholder from 'router/subrouters/DataRoom/components/FileCheckSelector/components/FileRowPlaceholder';
import { DataRoomSelectableNode } from 'router/subrouters/DataRoom/components/FileCheckSelector/types';
import {
  collectCheckedNodesForDownload,
  getIsShareableNodeDisabled,
} from 'router/subrouters/DataRoom/components/FileCheckSelector/utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { getHasElementsInArray, getIsArrayEmpty } from 'utils';

import './ShareableLinkItems.styles.scss';

const PLACEHOLDER_ROWS = 5;

type ShareableLinkItemsProps = {
  className?: string;
  isLoading?: boolean;
  nodes?: DataRoomSelectableNode[];
  initiallyCheckedNodes?: string[];
  // Shows what an existing link already shares, for review rather than authoring.
  readOnly?: boolean;
  setNodes: Dispatch<SetStateAction<DataRoomSelectableNode[]>>;
};

const ShareableLinkItems: React.FC<ShareableLinkItemsProps> = (props) => {
  const {
    className,
    isLoading,
    nodes = [],
    initiallyCheckedNodes = [],
    readOnly = false,
    setNodes,
  } = props;

  const classes = classNames(
    'shareable-link-items',
    { 'shareable-link-items--empty': !isLoading && getIsArrayEmpty(nodes) },
    className,
  );

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { t } = useTranslation();

  const hasCheckedNodes = useMemo(
    () => getHasElementsInArray(collectCheckedNodesForDownload(nodes)),
    [nodes],
  );

  const getChildren = useCallback(
    (parentId: string) => dataRoomApi.nodes.getChildren(dataRoomId, parentId),
    [dataRoomId],
  );

  return (
    <div className={classes}>
      {isLoading ? (
        <div className="shareable-link-items__placeholder">
          {Array(PLACEHOLDER_ROWS)
            .fill(null)
            .map((_, index) => (
              <FileRowPlaceholder key={index} />
            ))}
        </div>
      ) : getIsArrayEmpty(nodes) ? (
        <WarningBanner title={t('noFilesInDataRoom')} />
      ) : (
        <>
          {/* An authoring warning: there is nothing to fix about an existing
          link's contents from here. */}
          {!readOnly && !hasCheckedNodes && (
            <WarningBanner title={t('noFilesSelected')} />
          )}

          <FileCheckSelector
            dataRoomNodes={nodes}
            buyerLabel={t('onlySellSideFilesWillBeShared')}
            initiallyCheckedNodes={initiallyCheckedNodes}
            readOnly={readOnly}
            setDataRoomNodes={setNodes}
            // Rows a folder loads when opened are built here rather than from the
            // props above, so read-only has to be baked into them as well.
            checkIsNodeDisabled={
              readOnly ? () => true : getIsShareableNodeDisabled
            }
            getChildren={getChildren}
          />
        </>
      )}
    </div>
  );
};

export default ShareableLinkItems;
